import { createContext, useContext, useState, useCallback, ReactNode } from "react";

interface CourseContextData {
  courseId: string | null;
  courseName: string | null;
  nodeId: string | null;
  nodeName: string | null;
  treeId: string | null;
  pageType: "dashboard" | "course" | "node-lesson" | "node-exam" | "node-slides" | "learner" | "workspace" | "course-creation" | "other";
  workspaceId: string | null;
  workspaceName: string | null;
}

interface CourseContextValue extends CourseContextData {
  setCourseContext: (data: Partial<CourseContextData>) => void;
  clearCourseContext: () => void;
}

const defaultContext: CourseContextData = {
  courseId: null,
  courseName: null,
  nodeId: null,
  nodeName: null,
  treeId: null,
  pageType: "other",
  workspaceId: null,
  workspaceName: null,
};

const CourseContext = createContext<CourseContextValue | undefined>(undefined);

export function CourseProvider({ children }: { children: ReactNode }) {
  const [contextData, setContextData] = useState<CourseContextData>(defaultContext);

  const setCourseContext = useCallback((data: Partial<CourseContextData>) => {
    setContextData(prev => {
      const next = { ...prev, ...data };
      // Avoid state update when nothing changed to prevent unnecessary re-renders (stops workspace blink)
      if (
        prev.courseId === next.courseId &&
        prev.courseName === next.courseName &&
        prev.nodeId === next.nodeId &&
        prev.nodeName === next.nodeName &&
        prev.pageType === next.pageType &&
        prev.workspaceId === next.workspaceId &&
        prev.workspaceName === next.workspaceName
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const clearCourseContext = useCallback(() => {
    setContextData(defaultContext);
  }, []);

  return (
    <CourseContext.Provider value={{ ...contextData, setCourseContext, clearCourseContext }}>
      {children}
    </CourseContext.Provider>
  );
}

export function useCourseContext() {
  const context = useContext(CourseContext);
  if (context === undefined) {
    throw new Error("useCourseContext must be used within a CourseProvider");
  }
  return context;
}

